import type { BrandConfig, OwnerProfile, DocTemplate } from '../../types/docops';

/** Replace {{variable.path}} placeholders with brand config values */
export function applyBrandConfig(text: string, config: BrandConfig): string {
  return text.replace(/\{\{([\w.]+)\}\}/g, (match, key: string) => {
    let val: unknown = config;
    for (const part of key.split('.')) {
      if (val && typeof val === 'object') {
        val = (val as Record<string, unknown>)[part];
      } else {
        return match;
      }
    }
    if (val === undefined || val === null || val === '') return match;
    if (Array.isArray(val)) return val.join(', ');
    return String(val);
  });
}

/** Build tier configuration context block for generation prompts */
export function buildTierContext(config: BrandConfig): string {
  const { tiers } = config;
  if (!tiers) return '';
  const lines = ['', '=== TIER CONFIGURATION (from brand config) ==='];
  for (const [key, tier] of Object.entries(tiers)) {
    const label = key.replace('tier', 'Tier ');
    if (!tier.enabled) {
      lines.push(`${label}: DISABLED (omit from document entirely)`);
    } else if (tier.pricingModel === 'free') {
      lines.push(`${label} (${tier.name}): pricingModel=free — NO pricing, NO payment terms, NO refund policy for this tier`);
    } else if (tier.pricingModel === 'freemium') {
      lines.push(`${label} (${tier.name}): pricingModel=freemium — base service free, paid upgrade option`);
    } else {
      const price = tier.price ? ` price=${tier.price}` : '';
      const desc = tier.priceDescription ? ` (${tier.priceDescription})` : '';
      lines.push(`${label} (${tier.name}): pricingModel=paid${price}${desc}`);
    }
  }
  lines.push('=== END TIER CONFIGURATION ===');
  return lines.join('\n');
}

/** Build the system prompt including owner context and brand config */
export function buildSystemPrompt(owner: OwnerProfile | null, config: BrandConfig): string {
  const base = `You are a legal document specialist for a health-tech company. Generate professional, legally-sound document outlines and drafts. Use clear section headers, bullet points for key provisions, and flag critical items. Output in clean Markdown format. These are drafts for attorney review — include commentary and attorney notes where helpful.`;

  if (!owner) return base;

  return `${base}

OWNER CONTEXT (CONFIDENTIAL — never reference directly in generated documents):

Structure: ${owner.trustStructure.rationale}
Operational role: ${owner.operationalRole === 'none' ? 'Owner has NO operational role in any entity' : owner.operationalRole}
Protection requirements:
${owner.protectionNeeds.map(p => `- ${p}`).join('\n')}

Structural constraints:
${owner.trustStructure.restrictions.map(r => `- ${r}`).join('\n')}

Entity separation requirements:
${owner.otherEntities.map(e => `- ${e.name}: ${e.relationship} (separation required: ${e.separationRequired}). ${e.notes}`).join('\n')}

Key relationships:
${owner.keyRelationships.map(r => `- ${r.name} — ${r.role} (${r.status})`).join('\n')}

Owner preferences:
- Default structure: ${owner.preferences.defaultStructure}
- Default operational role: ${owner.preferences.defaultOperationalRole}
- Always include: ${owner.preferences.defaultProtections.join(', ')}

IMPORTANT: The owner context explains WHY the structure exists. Generated documents should REFLECT the structure (trust licensing, no owner operational role, independent management) but should NEVER reference the underlying reasons. Documents say "family trust for estate planning purposes" — they do not say why estate planning is a priority. Never mention the owner by name. Use "the trust," "the licensor," or "IP owner" where appropriate.

BRAND CONFIG:
- Brand: ${config.brandName} (${config.brandEntity})
- State: ${config.stateOfFormation}
- Vertical: ${config.marketVertical}
- Product: ${config.productType}
- CEO: ${config.ceoName || '{{ceoName}}'}
- Medical Director: ${config.medicalDirectorName || '{{medicalDirectorName}}'} (${config.pcEntityName})
- Trust: ${config.trustName || '{{trustName}}'}
- Telehealth: ${config.telehealthNetwork || '{{telehealthNetwork}}'}
- Payment: ${config.paymentProcessor || '{{paymentProcessor}}'}

CRITICAL FORMATTING RULE: For any value you do not know, use {{camelCaseVariable}} format. NEVER use [Address], [Date], [City, State ZIP], or any bracket [] notation. All placeholders MUST be in {{mustache}} format so they can be programmatically replaced.`;
}

/**
 * Deterministic content sanitizer — runs AFTER generation, BEFORE quality gate.
 * Fixes common AI output issues that Claude keeps producing despite prompt instructions:
 * 1. Replaces [bracket] placeholders with actual config values or {{mustache}} format
 * 2. Injects brand name if missing
 * 3. Removes duplicate long lines
 */
export function sanitizeContent(content: string, config: BrandConfig): string {
  let result = content;

  // 1. Replace known bracket placeholders with actual config values
  const replacements: [RegExp, string][] = [
    // Brand / Company
    [/\[(?:Company\s*Name|Brand\s*Name|Company|Brand)\]/gi, config.brandName || ''],
    [/\[(?:Entity\s*Name|LLC\s*Name|Entity|LLC)\]/gi, config.brandEntity || ''],
    [/\[(?:State(?:\s+of\s+Formation)?)\]/gi, config.stateOfFormation || ''],
    [/\[(?:EIN)\]/gi, config.ein || '{{ein}}'],
    // People
    [/\[(?:CEO(?:\s+Name)?)\]/gi, config.ceoName || '{{ceoName}}'],
    [/\[(?:Medical\s*Director(?:\s+Name)?|MD(?:\s+Name)?)\]/gi, config.medicalDirectorName || '{{medicalDirectorName}}'],
    [/\[(?:Trustee(?:\s+Name)?)\]/gi, config.trustTrusteeName || '{{trustTrusteeName}}'],
    [/\[(?:Trust\s*Name)\]/gi, config.trustName || '{{trustName}}'],
    [/\[(?:PC\s+(?:Entity\s+)?Name)\]/gi, config.pcEntityName || '{{pcEntityName}}'],
    [/\[(?:PC\s+Owner(?:\s+Name)?)\]/gi, config.pcOwnerName || '{{pcOwnerName}}'],
    // Dates / contacts — use config if available, else mustache
    [/\[(?:Effective\s*Date)\]/gi, config.effectiveDate || '{{effectiveDate}}'],
    [/\[(?:Date)\]/gi, config.effectiveDate || '{{effectiveDate}}'],
    [/\[(?:Address|Business\s*Address)\]/gi, '{{businessAddress}}'],
    [/\[(?:City,?\s*State(?:\s+ZIP)?(?:\s+(?:Zip\s*)?Code)?)\]/gi, '{{cityStateZip}}'],
    [/\[(?:Phone(?:\s+Number)?)\]/gi, '{{phoneNumber}}'],
    [/\[(?:Email(?:\s+Address)?)\]/gi, config.supportEmail || '{{emailAddress}}'],
    // Structural / filler
    [/\[(?:INSERT|TBD|PLACEHOLDER)\b[^\]]*\]/gi, ''],
    [/\[(?:Signature)\]/gi, '_________________________'],
    [/\[(?:Full\s*Name|Legal\s*Name)\]/gi, '{{fullName}}'],
    [/\[(?:Title)\]/gi, '{{title}}'],
    [/\[(?:County)\]/gi, '{{county}}'],
  ];

  for (const [pattern, replacement] of replacements) {
    result = result.replace(pattern, replacement);
  }

  // 2. Aggressive catch-all: strip ANY remaining [bracket text] that isn't a markdown link or footnote
  result = result.replace(/\[([^\]]{1,80})\](?!\()/g, (match, inner: string) => {
    const trimmed = inner.trim();
    // Keep underscore blanks like [____]
    if (/^_+$/.test(trimmed)) return match;
    // Keep footnote references like [1], [42]
    if (/^\d+$/.test(trimmed)) return match;
    // Keep markdown checkbox like [x] or [ ]
    if (/^[xX ]$/.test(trimmed)) return match;
    // For known placeholder words, convert to mustache
    if (/^[A-Z][A-Za-z\s']{0,40}$/.test(trimmed) && /name|date|address|amount|percent|number|title/i.test(trimmed)) {
      const camelCase = trimmed.toLowerCase().replace(/['\s]+(\w)/g, (_: string, c: string) => c.toUpperCase());
      return `{{${camelCase}}}`;
    }
    // For instruction-style brackets like [describe assets], [insert here], just remove brackets
    return trimmed;
  });

  // 3. Run applyBrandConfig to fill any {{mustache}} vars we just created
  result = applyBrandConfig(result, config);

  // 3b. Strip any remaining {{variables}} that weren't in the config (they'd fail fb-no-mustache)
  result = result.replace(/\{\{[a-zA-Z]+\}\}/g, (match) => {
    // Keep only known config variables that might be legitimately unfilled
    const knownUnfilled = ['businessAddress', 'cityStateZip', 'phoneNumber', 'emailAddress', 'county', 'fullName'];
    const varName = match.slice(2, -2);
    if (knownUnfilled.includes(varName)) return match;
    // Remove unknown mustache vars — they just create gate failures
    return varName.replace(/([A-Z])/g, ' $1').trim();
  });

  // 4. Ensure brand name appears — inject after first heading if missing
  if (config.brandName && !result.toLowerCase().includes(config.brandName.toLowerCase())) {
    result = result.replace(/^(#\s+.+)$/m, `$1\n\n**${config.brandName}**`);
  }

  // 5. Remove duplicate content blocks
  const lines = result.split('\n');
  const seen = new Set<string>();
  const seenH2 = new Set<string>();
  const deduped: string[] = [];
  let h1Count = 0;

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip duplicate H1 headings (keep first only)
    if (/^#\s+[^\n]+$/.test(trimmed)) {
      h1Count++;
      if (h1Count > 1) continue;
    }

    // Skip duplicate H2 headings (exact match)
    if (/^##\s+.+$/.test(trimmed)) {
      const key = trimmed.toLowerCase();
      if (seenH2.has(key)) continue;
      seenH2.add(key);
    }

    // Skip duplicate long lines
    if (trimmed.length > 60 && seen.has(trimmed)) continue;
    if (trimmed.length > 60) seen.add(trimmed);

    deduped.push(line);
  }

  return deduped.join('\n');
}

/** Merge AI output with template content (if template has boilerplate wrapping) */
export function applyTemplateContent(rawContent: string, tpl: DocTemplate, config: BrandConfig): string {
  if (tpl.qualityGate) return rawContent;
  if (!tpl.templateContent) return rawContent;
  const resolvedTemplate = applyBrandConfig(tpl.templateContent, config);
  if (resolvedTemplate.includes('{{AI_GENERATED_CONTENT}}')) {
    const sections = rawContent.split(/(?=^##\s)/m);
    let sectionIdx = 0;
    return resolvedTemplate.replace(/\{\{AI_GENERATED_CONTENT\}\}/g, () => {
      const section = sections[sectionIdx] || '';
      sectionIdx++;
      return section.trim();
    });
  }
  // If Claude already generated a complete document (starts with # heading),
  // don't prepend the template skeleton — it would create duplicate content
  if (/^\s*#\s+/m.test(rawContent)) {
    return rawContent;
  }
  return resolvedTemplate + '\n\n' + rawContent;
}
